import React, { useMemo, useState } from "react";
import { ArrowLeft, History, RotateCcw } from "lucide-react";
import CompanionAssessmentSummary from "@/components/daily/CompanionAssessmentSummary";
import RobustAssessmentProfileView from "@/components/assessment/RobustAssessmentProfileView";
import {
  computeArchetype,
  loadProfilesLocal,
} from "@/components/assessment/assessmentScoring";
import {
  buildRobustNervousSystemProfile,
  isRobustAssessment,
} from "@/components/assessment/robustAssessmentEngine";
import { AppCard, AppPageHeader } from "@/components/daily/CompanionAppUi";

const toAssessment = (record, fallbackIdentity) => {
  const profile = computeArchetype(record.answers || {});
  const nervousSystemProfile =
    record.nervousSystemProfile ||
    record.answers?.robustProfile ||
    profile.nervousSystemProfile ||
    null;
  return {
    id: record.id,
    schemaVersion: record.schemaVersion,
    assessmentModel: record.assessmentModel,
    answers: record.answers,
    identity: record.identity || fallbackIdentity,
    completedAt: record.completedAt,
    profile: nervousSystemProfile
      ? {
          key: nervousSystemProfile.legacyProfileKey,
          title: nervousSystemProfile.headline,
          body: nervousSystemProfile.bottomLine,
          nervousSystemProfile,
        }
      : profile,
    nervousSystemProfile,
  };
};

const CompanionAssessmentSummaryV2 = ({ assessment, onBack, onRetake }) => {
  const history = useMemo(() => loadProfilesLocal(), []);
  const initial =
    history.find(
      (record) => Number(record.completedAt) === Number(assessment.completedAt),
    ) || history[history.length - 1] || null;
  const [selectedId, setSelectedId] = useState(initial?.id || "");
  const selectedRecord =
    history.find((record) => record.id === selectedId) || initial;
  const selected = selectedRecord
    ? toAssessment(selectedRecord, assessment.identity)
    : assessment;
  const robust = isRobustAssessment(selected.answers || {});

  if (!robust) {
    return (
      <CompanionAssessmentSummary
        assessment={selected}
        onBack={onBack}
        onRetake={onRetake}
      />
    );
  }

  const profile =
    selected.nervousSystemProfile ||
    selected.profile?.nervousSystemProfile ||
    buildRobustNervousSystemProfile(selected.answers);

  return (
    <div data-testid="companion-robust-assessment-summary">
      <AppPageHeader
        eyebrow="Saved nervous-system assessment"
        title="Your nervous-system baseline and response profile"
        body="Review the active robust baseline, earlier assessments, the measurements underneath the interpretation, and the calculation context used by APOS Companion."
        onBack={onBack}
      />

      <AppCard tone="action">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="forr-overline">Active assessment</div>
            <h2 className="mt-2 font-display text-2xl forr-text-ink">
              {profile.headline}
            </h2>
            <p className="mt-2 text-sm forr-text-soft">
              Completed {new Date(selected.completedAt).toLocaleDateString()} · Assessment version {selected.schemaVersion || 5}
            </p>
          </div>
          <button
            type="button"
            onClick={onRetake}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full px-5 py-3 text-sm forr-btn-secondary"
          >
            <RotateCcw size={15} /> Take another assessment
          </button>
        </div>
      </AppCard>

      {history.length > 1 && (
        <AppCard className="mt-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <History size={21} className="forr-text-teal-deep" />
              <div>
                <div className="forr-overline">Assessment history</div>
                <p className="mt-2 text-sm leading-relaxed forr-text-soft">
                  Earlier assessments remain append-only and can be reopened without changing the active baseline.
                </p>
              </div>
            </div>
            <select
              value={selectedId}
              onChange={(event) => setSelectedId(event.target.value)}
              className="rounded-xl border border-black/10 bg-white px-3 py-2.5 text-sm forr-text-ink sm:min-w-[300px]"
            >
              {history
                .slice()
                .reverse()
                .map((record, index) => (
                  <option key={record.id} value={record.id}>
                    {new Date(record.completedAt).toLocaleDateString()} · {record.profileTitle || "Saved assessment"}{index === 0 ? " · Latest" : ""}
                  </option>
                ))}
            </select>
          </div>
        </AppCard>
      )}

      <div className="mt-4">
        <RobustAssessmentProfileView
          answers={selected.answers}
          profile={profile}
        />
      </div>

      <button
        type="button"
        onClick={onBack}
        className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm forr-btn-secondary"
      >
        <ArrowLeft size={15} /> Back to APOS Companion
      </button>
    </div>
  );
};

export default CompanionAssessmentSummaryV2;
